import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useData } from '@pure-ptr/react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Typography,
    Box,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
    TextField,
    FormControl,
    InputLabel,
    CircularProgress,
    Button,
    Alert,
    Snackbar
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import {
    Portlet,
    PortletBody,
    PortletHeader,
    PortletHeaderToolbar
} from "../../../partials/content/Portlet";
import { BackButton } from "../../../common/BackButton";
import { Loadable } from '../../../common';
import { getAccountConsolidationConfiguration, updateAccountConsolidationConfiguration, getFuzzyLogicSelectionCriteria } from '../../../store/api/facilities';
import { asDate } from '../../../util/date';
import FuzzyLogicSelectionCriteriaFieldArray from './FuzzyLogicSelectionCriteriaFieldArray';
import { Card } from "react-bootstrap";

const useStyles = makeStyles(theme => ({
    portlet: {
        boxShadow: 'none',
        margin: 8
    },
    color: {
        color: '#48465b'
    },
    formControl: {
        minWidth: 300,
        margin: theme.spacing(1, 0)
    },
    section: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    gridSection: {
        marginTop: theme.spacing(3)
    },
    tableContainer: {
        marginTop: theme.spacing(2)
    }
}));

interface RouteParams {
    facilityId: string;
}

interface FuzzyLogicCriteria {
    id: number;
    name: string;
    fieldName: string;
    fieldType: string;
    description: string | null;
    isMandatory?: boolean;
}

interface SelectedCriteria {
    id?: number;
    accountConsolidationConfigurationId?: number;
    fuzzyLogicSelectionCriteriaId: number;
    isSelected: boolean;
}

interface AccountConsolidationConfig {
    id: number;
    mergeClaims: boolean;
    facilityId: number;
    consolidationType: string;
    accountIdentifierNumbers: number;
    updatedBy: string;
    updatedAt: string;
    facility: any;
    fuzzyLogicSelectionCriteriaXref: SelectedCriteria[];
}

const AccountConsolidationConfigurationPage: React.FC = () => {
    const classes = useStyles();
    const { facilityId } = useParams<RouteParams>();
    const [editMode, setEditMode] = useState(false);
    const [editedConfig, setEditedConfig] = useState<AccountConsolidationConfig | null>(null);
    const [validationError, setValidationError] = useState<string>('');
    const [showValidationError, setShowValidationError] = useState(false);
    const [savePayload, setSavePayload] = useState<any>(null);

    // Load configuration and criteria
    const {
        data: configData,
        isPending: isLoadingConfig,
        error: loadError,
        reload: reloadConfiguration
    } = useData(async () => {
        if (!facilityId) return null;
        
        // Fetch available fuzzy logic criteria from API
        const criteriaResponse = await getFuzzyLogicSelectionCriteria();
        
        // Fetch configuration
        const response = await getAccountConsolidationConfiguration(facilityId);
        
        // Transform data for boolean selection - preserve IDs from API
        const transformedResponse = {
            ...response,
            fuzzyLogicSelectionCriteriaXref: response.fuzzyLogicSelectionCriteriaXref?.map((item: any) => ({
                id: item.id,
                accountConsolidationConfigurationId: item.accountConsolidationConfigurationId,
                fuzzyLogicSelectionCriteriaId: item.fuzzyLogicSelectionCriteriaId || item.fuzzyLogicSelectionCriteria?.id,
                isSelected: item.enabled !== undefined ? item.enabled : true
            })) || []
        };
        
        return {
            config: transformedResponse,
            availableCriteria: criteriaResponse || []
        };
    }, [facilityId]);

    const config = configData?.config || null;
    const availableCriteria = configData?.availableCriteria || [];

    // Save configuration
    const {
        isPending: isSaving,
        error: saveError
    } = useData(async () => {
        if (!savePayload) return;
        
        await updateAccountConsolidationConfiguration(facilityId, savePayload);
        setSavePayload(null);
        setEditMode(false);
        reloadConfiguration();
    }, [savePayload]);

    // Update editedConfig when config loads
    useEffect(() => {
        if (config && !editedConfig) {
            setEditedConfig(config);
        }
    }, [config]);

    const handleEdit = () => {
        setEditMode(true);
    };

    const handleCancel = () => {
        setEditMode(false);
        setEditedConfig(config);
        setValidationError('');
        setShowValidationError(false);
    };

    const validateCriteria = (): boolean => {
        // Validate Sequential consolidation type
        if (editedConfig?.consolidationType === 'Sequential') {
            const accountIdentifierNumbers = editedConfig.accountIdentifierNumbers;
            if (accountIdentifierNumbers <= 3 || accountIdentifierNumbers >= 20) {
                setValidationError('Account Identifier Numbers must be greater than 3 and less than 20');
                setShowValidationError(true);
                return false;
            }
            return true;
        }

        // No validation needed for Fuzzy Logic - just toggles
        return true;
    };

    const handleSave = () => {
        if (!editedConfig) return;

        // Validate criteria before saving
        if (!validateCriteria()) {
            return;
        }

        setValidationError('');
        setShowValidationError(false);
        
        // Transform data back to API format - send all criteria with enabled status
        const dataToSave = {
            ...editedConfig,
            fuzzyLogicSelectionCriteriaXref: editedConfig.consolidationType === 'Sequential' 
                ? [] 
                : availableCriteria.map(criteria => {
                    // Find if this criteria exists in edited config
                    const existing = editedConfig.fuzzyLogicSelectionCriteriaXref.find(
                        c => c.fuzzyLogicSelectionCriteriaId === criteria.id
                    );
                    
                    // Check if it's selected or mandatory
                    const isSelected = existing?.isSelected || criteria?.isMandatory || false;
                    
                    return {
                        id: existing?.id,
                        accountConsolidationConfigurationId: existing?.accountConsolidationConfigurationId,
                        fuzzyLogicSelectionCriteriaId: criteria?.id,
                        enabled: isSelected
                    };
                })
        };
        
        setSavePayload(dataToSave);
    };

    const handleCriteriaChange = (criteria: SelectedCriteria[]) => {
        if (editedConfig) {
            setEditedConfig({
                ...editedConfig,
                fuzzyLogicSelectionCriteriaXref: criteria
            });
        }
    };

    const handleFieldChange = (field: keyof AccountConsolidationConfig, value: any) => {
        if (editedConfig) {
            let updates: any = { [field]: value };
            
            // Handle consolidationType changes
            if (field === 'consolidationType') {
                if (value === 'Sequential') {
                    // Clear fuzzy logic criteria when switching to Sequential
                    updates.fuzzyLogicSelectionCriteriaXref = [];
                } else if (value === 'Fuzzy Logic') {
                    // Set Account Identifier Numbers to 0 when switching to Fuzzy Logic
                    updates.accountIdentifierNumbers = 0;
                    // Initialize with mandatory criteria selected
                    updates.fuzzyLogicSelectionCriteriaXref = availableCriteria
                        .filter(c => c.isMandatory)
                        .map(c => ({
                            fuzzyLogicSelectionCriteriaId: c.id,
                            isSelected: true
                        }));
                }
            }
            
            setEditedConfig({
                ...editedConfig,
                ...updates
            });
        }
    };

    // Show loading indicator while fetching data
    if (isLoadingConfig != null) {
        return (
            <Loadable loading={true}>
                <Portlet>
                    <PortletHeader
                        toolbar={
                            <PortletHeaderToolbar className='w-100'>
                                <BackButton defaultRoute="/cms/facilities" />
                            </PortletHeaderToolbar>
                        }
                    />
                    <PortletBody />
                </Portlet>
            </Loadable>
        );
    }

    // Show error message if no configuration found after loading
    if (!config) {
        return (
            <Portlet>
                    <PortletHeader
                        toolbar={
                            <PortletHeaderToolbar className='w-100'>
                                <BackButton defaultRoute="/cms/facilities" />
                            </PortletHeaderToolbar>
                        }
                    />
                    <PortletBody>
                        <Typography>No configuration found for this facility.</Typography>
                    </PortletBody>
                </Portlet>
        );
    }

    return (
        <Loadable loading={isSaving != null}>
            <Helmet title="Account Consolidation Configuration" />
            <Portlet>
                <PortletHeader
                    toolbar={
                        <PortletHeaderToolbar className='w-100 d-flex justify-content-between'>
                            <BackButton defaultRoute="/cms/facilities" />
                            <Box>
                                {!editMode ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<EditIcon />}
                                        onClick={handleEdit}
                                    >
                                        Edit
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="outlined"
                                            onClick={handleCancel}
                                            className="mr-2"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSave}
                                            disabled={isSaving != null}
                                        >
                                            {isSaving != null ? 'Saving...' : 'Save'}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody className="pt-2">
                    {/* Configuration Section */}
                    <div className={classes.color + " " + classes.section}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="row py-2">
                                    <div className="col-12 py-1">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={editedConfig?.mergeClaims || false}
                                                    onChange={(e) => handleFieldChange('mergeClaims', e.target.checked)}
                                                    color="primary"
                                                    disabled={!editMode}
                                                />
                                            }
                                            label="Merge Claims"
                                        />
                                    </div>
                                </div>

                                <div className="row py-2">
                                    <div className="col-12 py-1">
                                        <FormControl className={classes.formControl} fullWidth disabled={!editMode}>
                                            <InputLabel>Consolidation Type</InputLabel>
                                            <Select
                                                value={editedConfig?.consolidationType || ''}
                                                onChange={(e) => handleFieldChange('consolidationType', e.target.value)}
                                                label="Consolidation Type"
                                            >
                                                <MenuItem value="Sequential">Sequential</MenuItem>
                                                <MenuItem value="Fuzzy Logic">Fuzzy Logic</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>

                                <div className="row py-2">
                                    <div className="col-12 py-1">
                                        <TextField
                                            label="Account Identifier Numbers"
                                            value={editedConfig?.accountIdentifierNumbers || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*$/.test(value)) {
                                                    handleFieldChange('accountIdentifierNumbers', parseInt(value) || 0);
                                                }
                                            }}
                                            type="text"
                                            fullWidth
                                            disabled={!editMode || editedConfig?.consolidationType === 'Fuzzy Logic'}
                                            className={classes.formControl}
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                readOnly: editedConfig?.consolidationType === 'Fuzzy Logic'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="row py-2">
                                    <div className="col-12 py-1">
                                        <Typography variant="body2">
                                            <span className="font-weight-bold mr-1">Updated By: </span>
                                            <span>{config?.updatedBy}</span>
                                        </Typography>
                                    </div>
                                </div>

                                <div className="row py-2">
                                    <div className="col-12 py-1">
                                        <Typography variant="body2">
                                            <span className="font-weight-bold mr-1">Updated At: </span>
                                            <span>{asDate(config?.updatedAt)}</span>
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Fuzzy Logic Criteria Grid */}
                    {editedConfig && editedConfig.consolidationType === 'Fuzzy Logic' && (
                        <div className={classes.gridSection}>
                            <Card>
                                <Card.Header as="h5">Fuzzy Logic Criteria</Card.Header>
                                <Card.Body>
                                    <FuzzyLogicSelectionCriteriaFieldArray
                                        selectedCriteria={editedConfig.fuzzyLogicSelectionCriteriaXref || []}
                                        availableCriteria={availableCriteria}
                                        onChange={handleCriteriaChange}
                                        disabled={!editMode}
                                    />
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </PortletBody>
            </Portlet>
            
            <Snackbar
                open={showValidationError}
                autoHideDuration={6000}
                onClose={() => setShowValidationError(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowValidationError(false)}
                    severity="error"
                    variant="filled"
                >
                    {validationError}
                </Alert>
            </Snackbar>
        </Loadable>
    );
};

export default AccountConsolidationConfigurationPage;
